import { useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { queryService } from "../services/query-service";
import { APIRequestParams, AIResponse } from "../models/api-types";
import { QueryKeys } from "../config/query-keys";
import useAuthStore from "@/store/use-auth-store";
import { useModalStore } from "@/store/use-modal-store";
import {
  GetAddressesResponse,
  AddAddressPayload,
  AddAddressResponse,
  UpdateAddressPayload,
  CancelSubscriptionResponse,
  // 🛒 Shop/Cart API Types
  AddToCartRequest,
  AddToCartResponse,
  UpdateCartQuantityRequest,
  UpdateCartQuantityResponse,
  GetCartResponse,
  DeleteFromCartResponse,
  GetBrandByBrandIdResponse,
  CardInfoResponse,
  CheckoutAgreementsRequest,
  CheckoutAgreementsResponse,
  // Billing History API Types
  BillingHistoryResponse,
  // Saklı Kart API Types
  SavedCardsResponse,
  AddCardPayload,
  // 📍 Location API Types
  GetCountriesResponse,
  GetCitiesResponse,
  GetTownsResponse,
  GetQuartersResponse,
  MessageFeedbackRequest,
  MessageFeedbackResponse,
  // 🎁 Rewards API Types
  RewardsResponse,
  RewardHistoryResponse,
  RewardHistoryParams,
  ConvertRewardsRequest,
  ConvertRewardsResponse,
  PreviewConvertRewardsResponse,
  // 🛫 Cosmopoints API Types
  CosmopointsProgramListResponse,
  CosmopointsConnectionListResponse,
  CosmopointsConnectionResponse,
  CreateCosmopointsConnectionRequest,
} from "../models/api-types";

let lastGetAllCartChangesKey: string | null = null;

export function useQueryApi() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (params: APIRequestParams) => queryService.sendQuery(params),
    onSuccess: (data, variables) => {
      // Widget seçimlerinde benzersiz queryKey kullanın
      const textKey = variables.message || variables.prompt || "";
      const queryKey = variables.model
        ? [...QueryKeys.QUERY.byText(textKey), variables.model]
        : QueryKeys.QUERY.byText(textKey);

      queryClient.setQueryData(queryKey, data);

      // Son yanıtı da genel önbelleğe ekle
      queryClient.setQueryData(
        QueryKeys.QUERY.all,
        (old: AIResponse[] = []) => {
          // En son 10 sorguyu sakla
          const updated = [data, ...old];
          return updated.slice(0, 10);
        }
      );

      // 🚀 BACKEND INVALIDATION - Conversation messages cache'ini invalidate et
      // ❌ DEVRE DIŞI: Yanıt geldikten sonra conversation'ı tekrar çekme işlemi devre dışı bırakıldı
      // if (variables.chat_id) {
      //   queryClient.invalidateQueries({
      //     queryKey: QueryKeys.CONVERSATIONS.messages(variables.chat_id),
      //   });
      // }
    },
  });

  const sendQuery = async (
    message: string,
    _type?: string, // artık kullanılmıyor
    model?: string,
    chat_id?: string,
    _user_id?: string, // artık gönderilmiyor
    _message_id?: string // artık kullanılmıyor
  ): Promise<AIResponse> => {
    const params: APIRequestParams = {
      message,
      model,
      chat_id,
      stream: false,
    };

    return mutation.mutateAsync(params);
  };

  return {
    sendQuery,
    isLoading: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
  };
}

// 🗳️ Message feedback (like/dislike)
export function useMessageFeedback() {
  return useMutation<MessageFeedbackResponse, unknown, MessageFeedbackRequest>({
    mutationFn: (payload) => queryService.updateMessageFeedback(payload),
  });
}

// Adresleri getir
export function useGetAddresses() {
  return useQuery<GetAddressesResponse>({
    queryKey: QueryKeys.ADDRESS.all,
    queryFn: () => queryService.getAddresses(),
  });
}

// Adres ekle
export function useAddAddress() {
  const queryClient = useQueryClient();
  return useMutation<AddAddressResponse, unknown, AddAddressPayload>({
    mutationFn: (payload) => queryService.addAddress(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.ADDRESS.all });
    },
  });
}

// Adres güncelle
export function useUpdateAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      addressData,
    }: {
      id: string;
      addressData: UpdateAddressPayload;
    }) => queryService.updateAddress(id, addressData), // PUT method kullanıyor
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.ADDRESS.all });
    },
  });
}

// Adres sil
export function useDeleteAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => queryService.deleteAddress(addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.ADDRESS.all });
    },
  });
}

// 📍 Location API Hooks

// Ülkeleri getir
export function useCountries() {
  return useQuery<GetCountriesResponse>({
    queryKey: ["location", "countries"],
    queryFn: async () => {
      const response = await queryService.getLocation({ type: "country" });
      return response as GetCountriesResponse;
    },
    staleTime: 30 * 60 * 1000, // 30 dakika cache
    gcTime: 60 * 60 * 1000, // 1 saat garbage collection
  });
}

// Şehirleri getir
export function useCities(countryId: number | null) {
  return useQuery<GetCitiesResponse>({
    queryKey: ["location", "cities", countryId],
    queryFn: async () => {
      if (!countryId) {
        throw new Error("Country ID is required");
      }
      const response = await queryService.getLocation({
        type: "cities",
        countryId,
      });
      return response as GetCitiesResponse;
    },
    enabled: !!countryId, // Sadece countryId varsa çalışır
    staleTime: 30 * 60 * 1000, // 30 dakika cache
    gcTime: 60 * 60 * 1000, // 1 saat garbage collection
  });
}

// Bölgeleri getir
export function useTowns(countryId: number | null, cityId: number | null) {
  return useQuery<GetTownsResponse>({
    queryKey: ["location", "towns", countryId, cityId],
    queryFn: async () => {
      if (!countryId || !cityId) {
        throw new Error("Country ID and City ID are required");
      }
      const response = await queryService.getLocation({
        type: "towns",
        countryId,
        cityId,
      });
      return response as GetTownsResponse;
    },
    enabled: !!countryId && !!cityId, // Sadece countryId ve cityId varsa çalışır
    staleTime: 30 * 60 * 1000, // 30 dakika cache
    gcTime: 60 * 60 * 1000, // 1 saat garbage collection
  });
}

// Mahalleleri getir
export function useQuarters(
  countryId: number | null,
  cityId: number | null,
  townId: number | null
) {
  return useQuery<GetQuartersResponse>({
    queryKey: ["location", "quarters", countryId, cityId, townId],
    queryFn: async () => {
      if (!countryId || !cityId || !townId) {
        throw new Error("Country ID, City ID and Town ID are required");
      }
      const response = await queryService.getLocation({
        type: "quarters",
        countryId,
        cityId,
        townId,
      });
      return response as GetQuartersResponse;
    },
    enabled: !!countryId && !!cityId && !!townId, // Sadece tüm ID'ler varsa çalışır
    staleTime: 30 * 60 * 1000, // 30 dakika cache
    gcTime: 60 * 60 * 1000, // 1 saat garbage collection
  });
}

// 🛒 Shop/Cart API Hooks

// Sepete ürün ekle
export function useAddToCart() {
  const queryClient = useQueryClient();
  return useMutation<AddToCartResponse, unknown, AddToCartRequest>({
    mutationFn: (request) => queryService.addToCart(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.CART.items() });
    },
  });
}

// Sepet ürün miktarını güncelle
export function useUpdateCartQuantity() {
  const queryClient = useQueryClient();
  return useMutation<
    UpdateCartQuantityResponse,
    unknown,
    { cartId: string; request: UpdateCartQuantityRequest }
  >({
    mutationFn: ({ cartId, request }) =>
      queryService.updateCartQuantity(cartId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.CART.items() });
    },
  });
}

// Tüm sepet ürünlerini getir (normal query)
// offerId verilirse sadece o teklife ait ürünü döner (hemen al akışı)
// quantity verilirse belirlenen adet kadar döner
export function useGetAllCart(offerId?: string, quantity?: number) {
  const { openCartChanges } = useModalStore();
  const query = useQuery<GetCartResponse>({
    queryKey: offerId
      ? [...QueryKeys.CART.items(), "buyNow", offerId, quantity ?? 1]
      : [...QueryKeys.CART.items()],
    queryFn: () => queryService.getAllCart(offerId, quantity),
  });

  useEffect(() => {
    const changes = query.data?.data?.changes;
    if (Array.isArray(changes) && changes.length > 0) {
      const changesKey = JSON.stringify(changes);
      if (lastGetAllCartChangesKey !== changesKey) {
        lastGetAllCartChangesKey = changesKey;
        openCartChanges(changes);
      }
    } else {
      lastGetAllCartChangesKey = null;
    }
  }, [query.data, openCartChanges]);

  return query;
}

// Sepetten ürün sil
export function useDeleteFromCart() {
  const queryClient = useQueryClient();
  return useMutation<DeleteFromCartResponse, unknown, string>({
    mutationFn: (cartId) => queryService.deleteFromCart(cartId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.CART.items() });
    },
  });
}

// 🛍️ Brand API Hooks
export function useGetBrandByBrandId(brandId: string | number | null) {
  return useQuery<GetBrandByBrandIdResponse>({
    queryKey: ["brandByBrandId", brandId],
    enabled: Boolean(brandId),
    queryFn: () => queryService.getBrandByBrandId(brandId as string),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// 🧾 Checkout - Card Info by BIN
export function useCardInfo(
  bin: string | null,
  buyNow?: { offerId: string; quantity: number }
) {
  return useQuery<CardInfoResponse>({
    queryKey: [...QueryKeys.CHECKOUT.cardInfo(bin || ""), buyNow?.offerId ?? null],
    enabled: Boolean(bin && bin.length === 8),
    queryFn: () => queryService.getCardInfoByBin(bin as string, buyNow),
    staleTime: 0, // Kart değişiminde her zaman güncel taksit verisini al
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// 🧾 Checkout - Agreements by address (and optional buy-now offer)
export function useCheckoutAgreements(
  request: CheckoutAgreementsRequest | null,
  offerId?: string | null
) {
  const addressKey = request
    ? [
        request.shippingAddress.label,
        request.shippingAddress.addressLine1,
        request.shippingAddress.addressLine2 || "",
        request.shippingAddress.city,
        request.shippingAddress.state,
        request.shippingAddress.postalCode,
        request.shippingAddress.country,
      ].join("|")
    : "";

  return useQuery<CheckoutAgreementsResponse>({
    queryKey: QueryKeys.CHECKOUT.agreements(
      offerId ?? null,
      addressKey,
      request?.region || ""
    ),
    enabled: Boolean(request),
    queryFn: () =>
      queryService.getCheckoutAgreements(
        request as CheckoutAgreementsRequest,
        offerId ?? undefined
      ),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// 💳 Saklı Kartlar API Hook
export function useGetSavedCards() {
  const { isAuthenticated } = useAuthStore();
  return useQuery<SavedCardsResponse>({
    queryKey: QueryKeys.CARDS.saved(),
    queryFn: () => queryService.getSavedCards(),
    enabled: isAuthenticated(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// 💳 Kart Ekleme API Hook
export function useAddCard() {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; message: string }, unknown, AddCardPayload>({
    mutationFn: (payload) => queryService.addCard(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.CARDS.saved() });
    },
  });
}

// 💳 Kart Silme API Hook
export function useDeleteCard() {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; message: string }, unknown, string>({
    mutationFn: (cardToken) => queryService.deleteCard(cardToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.CARDS.saved() });
    },
  });
}

// 💳 Billing History API Hook
export function useBillingHistory() {
  const { isAuthenticated } = useAuthStore();

  return useQuery<BillingHistoryResponse>({
    queryKey: ["billingHistory"],
    queryFn: () => queryService.getBillingHistory(),
    enabled: isAuthenticated(), // Sadece auth olan kullanıcılar için
    staleTime: 5 * 60 * 1000, // 5 dakika
    gcTime: 15 * 60 * 1000, // 15 dakika
    refetchOnWindowFocus: false,
  });
}

// 💳 Cancel Subscription API Hook
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation<CancelSubscriptionResponse, unknown, string>({
    mutationFn: (reason) => queryService.cancelSubscription(reason),
    onSuccess: () => {
      // Billing data'yı refresh et
      queryClient.invalidateQueries({ queryKey: ["billingHistory"] });
      // User data'yı da refresh et (credits değişebilir)
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

// 🎁 Rewards API Hooks
export function useRewards() {
  const { isAuthenticated } = useAuthStore();

  return useQuery<RewardsResponse>({
    queryKey: QueryKeys.REWARDS.balances(),
    queryFn: () => queryService.getRewards(),
    enabled: isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 dakika
    gcTime: 15 * 60 * 1000, // 15 dakika
    refetchOnWindowFocus: false,
  });
}

export function useRewardsHistory(params: RewardHistoryParams = {}) {
  const { isAuthenticated } = useAuthStore();
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;

  return useQuery<RewardHistoryResponse>({
    queryKey: QueryKeys.REWARDS.history(page, limit),
    queryFn: () => queryService.getRewardsHistory({ page, limit }),
    enabled: isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 dakika
    gcTime: 15 * 60 * 1000, // 15 dakika
    refetchOnWindowFocus: false,
  });
}

export function useConvertRewards() {
  const queryClient = useQueryClient();
  return useMutation<ConvertRewardsResponse, unknown, ConvertRewardsRequest>({
    mutationFn: (payload) => queryService.convertRewards(payload),
    onSuccess: () => {
      // Reward bakiyesi ve geçmişi değişti
      queryClient.invalidateQueries({ queryKey: QueryKeys.REWARDS.all });
      // Harmony credit'leri değişti
      queryClient.invalidateQueries({ queryKey: ["billingHistory"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

/**
 * Quotes both conversion destinations for an amount of credits.
 *
 * Debounce `amount` at the call site — this fires a request per change, and
 * the points leg is a live call to the partner program.
 */
export function usePreviewConvertRewards(
  amount: number,
  connectionId?: string,
  enabled = true
) {
  const { isAuthenticated } = useAuthStore();

  return useQuery<PreviewConvertRewardsResponse>({
    queryKey: QueryKeys.REWARDS.convertPreview(amount, connectionId),
    queryFn: () =>
      queryService.previewConvertRewards({ amount, connectionId }),
    enabled: enabled && isAuthenticated() && amount > 0,
    staleTime: 60 * 1000, // 1 dakika — kur çok sık değişmiyor
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// 🛫 Cosmopoints API Hooks
export function useCosmopointsPrograms(enabled = true) {
  const { isAuthenticated } = useAuthStore();

  return useQuery<CosmopointsProgramListResponse>({
    queryKey: QueryKeys.COSMOPOINTS.programs(),
    queryFn: () => queryService.getCosmopointsPrograms(),
    enabled: enabled && isAuthenticated(),
    // Katalog verisi; sunucu tarafında da 1 saat cache'leniyor.
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useCosmopointsConnections(enabled = true) {
  const { isAuthenticated } = useAuthStore();

  return useQuery<CosmopointsConnectionListResponse>({
    queryKey: QueryKeys.COSMOPOINTS.connections(),
    queryFn: () => queryService.getCosmopointsConnections(),
    enabled: enabled && isAuthenticated(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useCreateCosmopointsConnection() {
  const queryClient = useQueryClient();
  return useMutation<
    CosmopointsConnectionResponse,
    unknown,
    CreateCosmopointsConnectionRequest
  >({
    mutationFn: (payload) => queryService.createCosmopointsConnection(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.COSMOPOINTS.all });
      // Yeni bağlantı, puan teklifini de değiştirir.
      queryClient.invalidateQueries({ queryKey: QueryKeys.REWARDS.all });
    },
  });
}

export function useDeleteCosmopointsConnection() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => queryService.deleteCosmopointsConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.COSMOPOINTS.all });
      queryClient.invalidateQueries({ queryKey: QueryKeys.REWARDS.all });
    },
  });
}

// 🔔 Update Notification Settings API Hook
export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => queryService.updateNotificationSettings(data),
    onSuccess: () => {
      // User data'yı refresh et (notifications user data'da)
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}
